import * as ARS from "../ars_ldm";
import { ArsSelect } from "../inputs/ArsSelect";
import { ArsText } from "../inputs/ArsText";
import { ArsVariable } from "../inputs/ArsVariable";
import { normalizeParameterName } from "../utility";

/**
 * Template parameter metadata and the table item whose stored value is edited.
 */
export interface ArsTableItemParameterProps {
  index: number;
  dataPath: string;
  disabled?: boolean;
  parameter: ARS.TemplateCodeParameter;
}

/**
 * Disambiguates and renders the appropriate input control for a given parameter in an ARS table item.
 *
 * @param props - Props for the component
 * @param props.dataPath - Base data path for the parameter value
 * @param props.disabled - Whether the input should be disabled
 * @param props.index - Index of the parameter in the list
 * @param props.parameter - The parameter object containing name, label, type, and available values
 */
export const ArsTableItemParameterValue = ({
  index,
  dataPath,
  disabled,
  parameter,
}: ArsTableItemParameterProps): React.ReactElement => {
  const baseDataPath = `${dataPath}/parameters`;
  // The back end camel-cases dictionary keys, so the saved value lives under the normalised name.
  // Names are normalised when edited, but this also covers parameters saved before that change.
  const parameterPath = `${baseDataPath}/${normalizeParameterName(parameter.name)}`;
  const idRoute = `${dataPath.replace(/\//g, "-")}-parameter-${index}`;
  const label = parameter.label ?? parameter.name;

  return (
    <>
      {parameter.parameterType === "select" || parameter.parameterType === "multiSelect" ? (
        <ArsSelect
          dataPath={parameterPath}
          tableDataPath
          id={`${idRoute}-select`}
          label={label}
          labelWidth={5}
          options={(parameter.availableValues ?? []).map((value) => ({
            value,
            label: value,
          }))}
          multiple={parameter.parameterType === "multiSelect"}
          disabled={disabled || (parameter.availableValues ?? []).length === 0}
        />
      ) : parameter.parameterType === "boolean" ? (
        <ArsSelect
          dataPath={parameterPath}
          tableDataPath
          id={`${idRoute}-boolean`}
          label={label}
          labelWidth={5}
          options={[
            { value: "true", label: "True" },
            { value: "false", label: "False" },
          ]}
          disabled={disabled}
        />
      ) : parameter.parameterType === "dataset.variable" ? (
        <ArsVariable
          combinedDataPath={parameterPath}
          tableDataPath
          id={`${idRoute}-dataset-variable`}
          label={label}
          labelWidth={5}
          disabled={disabled}
        />
      ) : (
        <ArsText
          dataPath={parameterPath}
          tableDataPath
          id={`${idRoute}-text`}
          label={label}
          labelWidth={5}
          placeholder={`Enter ${label}`}
          isArray={
            parameter.parameterType === "stringList" || parameter.parameterType === "numberList"
          }
          disabled={disabled}
        />
      )}
    </>
  );
};

ArsTableItemParameterValue.displayName = "ArsTableItemParameter";
